// =====================================================
// App router — Phase-14 R3 BrowserRouter migration.
//
// History
// -------
// Until Phase 14, this repo used a hand-rolled hash router
// (`window.location.hash` + a switch statement in App.tsx). Hash
// routing hides every internal path from Google + breaks deep-link
// sharing for prerenderable pages. R3 switches to react-router-dom v6
// BrowserRouter without touching the 49 page components — they still
// accept the existing `go(id, param)` callback prop.
//
// The trick: `useGo()` returns a function with the exact old
// signature; internally it calls `useNavigate()`. Pages call
// `go("catalog")` exactly like before; the function now does
// `navigate("/catalog")` instead of `window.location.hash = "#catalog"`.
//
// Each route renders through `<RouteShell>`, which calls `useGo()`
// once per render and forwards `go` + the route's path param (if any)
// to the page. Param naming preserves the old prop names
// (`courseId`, `assessmentId`, `labId`) so pages don't change.
//
// Phase-14.5 C3: dropped @ts-nocheck. useGo's signature is now the
// authoritative typed contract for the 140 call sites; Phase 15
// per-page TS cleanup will extend the page-component types to consume
// it via `import type { Go } from "../router"`.
// =====================================================

import React from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { UIRoot } from "./ui";
import { AppShell } from "./layouts/AppShell";
// S-21 — RoleHomeRedirect resolves the active role's real home.
import { ROLES, useRole } from "./role";

// =====================================================
// Phase-A R7.1.b — route-level code splitting
// =====================================================
//
// Eagerly-imported files (stay in the main bundle):
//   - Home.tsx, Programs.tsx, Admissions.tsx  (PUBLIC entry points
//     that we need to paint fast on cold loads)
//   - Auth.tsx                                 (AUTH_FLOW entry point;
//     login is a frequent cold-load destination — keeping LoginPage
//     in the main chunk avoids a Suspense flicker on the most common
//     authed-user entry sequence)
//   - More.tsx, Roles.tsx, Academic.tsx        (MIXED files containing
//     both PUBLIC + WORKSPACE exports. Splitting them would require
//     a file-level refactor; keeping them eager preserves the public
//     pages' cold-paint speed and accepts the marginal main-bundle
//     cost. Follow-on R can split these if/when needed.)
//
// Lazy-imported files (each becomes its own chunk, downloaded on
// first navigation to a route inside the file):
//   - All purely-workspace single-page files (Dashboard, Classroom,
//     etc.) and the dedicated role-dashboards (SuperAdminDashboard,
//     ContentManagerDashboard, etc.).
//   - University.tsx + Productivity.tsx (no PUBLIC exports inside).
//   - Settings.tsx, Audit.tsx, etc.
//
// Each React.lazy(() => import(...)) creates a Rollup chunk boundary.
// The chunk hash is content-derived, so successive deploys that don't
// change a workspace page leave its chunk cached client-side.

import HomePage from "./pages/Home";
import ProgramsPage from "./pages/Programs";
// R-NAV-3 (spec §4.5) — the mock /admissions wizard (pages/Admissions) is
// retired; /admissions now redirects to the real /apply form, so the page is
// no longer imported here.
// R-NAV-2 — single shared "coming soon" page for every not-yet-built sidebar
// destination. Eager (tiny, no data) so the ~50 placeholder routes don't each
// spin up a Suspense boundary.
import ComingSoonPage from "./pages/ComingSoon";
import { PLACEHOLDER_ROUTES } from "./router/placeholder-routes";
// R-NAV-6 — real 404. Eager + mounted OUTSIDE the AppShell layout (see below)
// so unknown URLs show a 404 instead of the landing page, and anon visitors
// aren't bounced to /login by the workspace auth gate.
import NotFoundPage from "./pages/NotFound";
import {
  LoginPage,
  ForgotPage,
  VerifyEmailPage,
  TwoFactorPage,
  OnboardingPage,
} from "./pages/Auth";
import {
  CalendarPage,
  LibraryPage,
  HelpPage,
  PricingPage,
  FacultyPage,
} from "./pages/More";
import {
  EventsPage,
  AboutPage,
} from "./pages/Roles";
import {
  AlumniPage,
  HackathonsPage,
  HonorCodePage,
} from "./pages/Academic";

// Lazy — purely-workspace files. Each `() => import(...)` becomes its
// own Rollup chunk. The named-export wrappers (`.then(m => ({ default: ... }))`)
// are needed for multi-export files; single-default-export files can
// use the shorter form.
const AssessmentLivePage = React.lazy(() => import("./pages/AssessmentLive"));
const ExamRoomPage = React.lazy(() => import("./pages/ExamRoom"));
const ProctorReviewPage = React.lazy(() => import("./pages/ProctorReview"));
const CatalogPage = React.lazy(() => import("./pages/Catalog"));
const CourseLivePage = React.lazy(() => import("./pages/CourseLive"));
const MyCoursesPage = React.lazy(() => import("./pages/MyCourses"));
const ProgressPage = React.lazy(() => import("./pages/Progress"));
// S-19 — /tutor now redirects into /agents (single assistant origin); the Tutor
// page component is retained on disk but no longer routed directly.
const AgentsPage = React.lazy(() => import("./pages/Agents"));
const CheckoutPage = React.lazy(() => import("./pages/Checkout")); // S9
const ClassroomPage = React.lazy(() => import("./pages/Classroom"));
// §12 — self-hosted WebRTC live class (NEW routes; additive, leaves /classroom intact).
const LiveLobbyPage = React.lazy(() => import("./pages/LiveLobby"));
const LiveSessionPage = React.lazy(() => import("./pages/LiveSession"));
const LiveClassesPage = React.lazy(() => import("./pages/LiveClasses"));
const SessionReportPage = React.lazy(() => import("./pages/SessionReport"));
const WalletPage = React.lazy(() => import("./pages/Wallet"));
// S-21 — pages/Dashboard.tsx (the legacy mock) is no longer routed; /dashboard
// renders RoleHomeRedirect instead (see the route entry).
// S-21 — the course HUB (tabs: overview/content/sessions/assignments/exams/
// grades/attendance/files/group/agent) now owns /course/:courseId; the old
// content player (pages/Course.tsx) stays alive as the hub's «محتوا» tab
// (embedded mode — imported statically by CourseHub, same chunk).
const CourseHubPage = React.lazy(() => import("./pages/CourseHub"));
const InstructorPage = React.lazy(() => import("./pages/Instructor"));
const CredentialPage = React.lazy(() => import("./pages/CredentialsRich"));
const SearchPage = React.lazy(() => import("./pages/Search"));
const AssessmentPage = React.lazy(() => import("./pages/Assessment"));
const CommunityPage = React.lazy(() => import("./pages/CommunityRich"));
const AnalyticsPage = React.lazy(() => import("./pages/Analytics"));
const AuthoringPage = React.lazy(() => import("./pages/Authoring"));
const RecordingsPage = React.lazy(() => import("./pages/Recordings"));
const AuditPage = React.lazy(() => import("./pages/Audit"));
const SettingsPage = React.lazy(() => import("./pages/Settings"));
const SuperAdminDashboard = React.lazy(() => import("./pages/dashboards/SuperAdminDashboard"));
const ContentManagerDashboard = React.lazy(() => import("./pages/dashboards/ContentManagerDashboard"));
const TADashboard = React.lazy(() => import("./pages/dashboards/TADashboard"));
const SupportDashboard = React.lazy(() => import("./pages/dashboards/SupportDashboard"));
const ModeratorDashboard = React.lazy(() => import("./pages/dashboards/ModeratorDashboard"));
const FacultyAdminDashboard = React.lazy(() => import("./pages/dashboards/FacultyAdminDashboard"));
const ParentDashboard = React.lazy(() => import("./pages/dashboards/ParentDashboard"));
// S-NRI-3.5 — dedicated homes for the 7 organizational/oversight roles (they
// were all landing on the student /progress page). Real read-only org data.
const MinistrySupervisorDashboard = React.lazy(() => import("./pages/dashboards/MinistrySupervisorDashboard"));
const HoldingManagerDashboard = React.lazy(() => import("./pages/dashboards/HoldingManagerDashboard"));
const CompanyAdminDashboard = React.lazy(() => import("./pages/dashboards/CompanyAdminDashboard"));
const CompanyTrainingDashboard = React.lazy(() => import("./pages/dashboards/CompanyTrainingDashboard"));
const AuditorDashboard = React.lazy(() => import("./pages/dashboards/AuditorDashboard"));
const CertificationDeskDashboard = React.lazy(() => import("./pages/dashboards/CertificationDeskDashboard"));
const AssessorDashboard = React.lazy(() => import("./pages/dashboards/AssessorDashboard"));
// S-NRI-ADAPT — «سازندهٔ پکیج» studio home (placeholder «استودیو دانش»). Real
// studio/payout land with S-NRI-6 / the STUDIO packages.
const CreatorStudioDashboard = React.lazy(() => import("./pages/dashboards/CreatorStudioDashboard"));
// Student-1 — real degree audit (replaces the Academic.tsx mock at /degree-audit).
const DegreeAuditPage = React.lazy(() => import("./pages/DegreeAudit"));
// چارت درسی من — the student's own گرایش-filtered degree chart (passed/in-progress/
// remaining by term), from GET /v1/registration/my-chart. Own lazy chunk.
const MyChartPage = React.lazy(() => import("./pages/MyChart"));
// Student services — rich, interactive sample-data pages (+ «در دست پیاده‌سازی» badge).
const OfficeHoursPage = React.lazy(() => import("./pages/OfficeHoursRich"));
const CareerPage = React.lazy(() => import("./pages/CareerRich"));
const WellnessPage = React.lazy(() => import("./pages/WellnessRich"));
// WELL-1 — the test-taking experience (intro+consent → questions+autosave →
// route to the result). Own lazy chunk (D66 Path D); /wellness dashboard stays.
const WellnessTestPage = React.lazy(() => import("./pages/WellnessTest"));
// WELL-1 — wellness test catalogue (grouped by category) + result detail +
// self-only result history. Each its own lazy chunk; /wellness dashboard stays.
const WellnessTestsPage = React.lazy(() => import("./pages/WellnessTests"));
const WellnessResultPage = React.lazy(() => import("./pages/WellnessResult"));
const WellnessResultsPage = React.lazy(() => import("./pages/WellnessResults"));
// WELL-4 — «مشاور دانشگاه» (counselor) workspace. The least-privilege wellness
// role's home: own appointments + the tenant's open crisis queue + a scoped
// student wellness summary. Own lazy chunk; created by the WELL-4 fe agent.
const CounselorInboxPage = React.lazy(() => import("./pages/CounselorInbox"));
// R-MON W9 — counselor caseload / monitoring (کاربرگ مراجعان). The least-privilege
// reporting layer: an aggregate roll-up of the counselor's OWN caseload + a roster
// with a per-student continuity timeline + schedule-follow-up. All scoped server-side.
const CounselorCaseloadPage = React.lazy(() => import("./pages/CounselorCaseload"));
const CognitiveProfilePage = React.lazy(() => import("./pages/CognitiveProfile"));
const AdaptivePathPage = React.lazy(() => import("./pages/AdaptivePath"));
const AssignmentsPage = React.lazy(() => import("./pages/AssignmentsRich"));
// Instructor pages — rich interactive sample-data teaching tools (+ badge).
const InstructorDashboardPage = React.lazy(() => import("./pages/InstructorDashboard"));
const QuestionBankPage = React.lazy(() => import("./pages/QuestionBank"));
const AssessmentBuilderPage = React.lazy(() => import("./pages/AssessmentBuilder"));
const RubricBuilderPage = React.lazy(() => import("./pages/RubricBuilder"));
const ClassRosterPage = React.lazy(() => import("./pages/ClassRoster"));
const AttendancePage = React.lazy(() => import("./pages/Attendance"));
const ClassAnalyticsPage = React.lazy(() => import("./pages/ClassAnalytics"));
const ItemAnalysisPage = React.lazy(() => import("./pages/ItemAnalysis"));
const InstructorCopilotPage = React.lazy(() => import("./pages/InstructorCopilot"));
// Faculty-admin pages — rich interactive sample-data tools (+ badge).
const CourseCatalogMasterPage = React.lazy(() => import("./pages/CourseCatalogMaster"));
const InstructorAssignmentPage = React.lazy(() => import("./pages/InstructorAssignment"));
const ProbationReviewPage = React.lazy(() => import("./pages/ProbationReview"));
const FacultyReportsPage = React.lazy(() => import("./pages/FacultyReports"));
// Admin + super_admin pages.
const AdminDashboardPage = React.lazy(() => import("./pages/AdminDashboard"));
const RolesPermissionsPage = React.lazy(() => import("./pages/RolesPermissions"));
const AdminCalendarPage = React.lazy(() => import("./pages/AdminCalendar"));
const IntegrationsPage = React.lazy(() => import("./pages/Integrations"));
const NotifTemplatesPage = React.lazy(() => import("./pages/NotifTemplates"));
const BroadcastPage = React.lazy(() => import("./pages/Broadcast"));
const AiGovernancePage = React.lazy(() => import("./pages/AiGovernance"));
const SystemMonitoringPage = React.lazy(() => import("./pages/SystemMonitoring"));
const AdminAnalyticsPage = React.lazy(() => import("./pages/AdminAnalytics"));
const TenantsAdminPage = React.lazy(() => import("./pages/TenantsAdmin"));
const ImpersonatePage = React.lazy(() => import("./pages/Impersonate"));
const MultitenancyPage = React.lazy(() => import("./pages/Multitenancy"));
// Support / TA / moderator / content_manager / org / parent pages.
const TicketsQueuePage = React.lazy(() => import("./pages/TicketsQueue"));
const UserLookupPage = React.lazy(() => import("./pages/UserLookup"));
const GradingQueuePage = React.lazy(() => import("./pages/GradingQueue"));
const FlaggedReportsPage = React.lazy(() => import("./pages/FlaggedReports"));
const CommunityModerationPage = React.lazy(() => import("./pages/CommunityModeration"));
const AutoRulesPage = React.lazy(() => import("./pages/AutoRules"));
const ModerationLogPage = React.lazy(() => import("./pages/ModerationLog"));
const ContentReviewPage = React.lazy(() => import("./pages/ContentReview"));
const ContentAnalyticsPage = React.lazy(() => import("./pages/ContentAnalytics"));
const OrgUsersPage = React.lazy(() => import("./pages/OrgUsers"));
const OrgBulkEnrollPage = React.lazy(() => import("./pages/OrgBulkEnroll"));
const OrgSeatsPage = React.lazy(() => import("./pages/OrgSeats"));
const OrgInvoicesPage = React.lazy(() => import("./pages/OrgInvoices"));
const OrgReportsPage = React.lazy(() => import("./pages/OrgReports"));
const ParentGradesPage = React.lazy(() => import("./pages/ParentGrades"));
const ParentAttendancePage = React.lazy(() => import("./pages/ParentAttendance"));
const ParentSchedulePage = React.lazy(() => import("./pages/ParentSchedule"));
const ParentTuitionPage = React.lazy(() => import("./pages/ParentTuition"));
// Student-1b — real weekly schedule (replaces the ComingSoon at /schedule).
const WeeklySchedulePage = React.lazy(() => import("./pages/WeeklySchedule"));
// Student-1c — real academic calendar (active term's process windows: انتخاب واحد /
// حذف‌واضافه / امتحانات). Reachable at /academic-calendar.
const AcademicCalendarPage = React.lazy(() => import("./pages/AcademicCalendar"));
// R-CAL2 — تقویم یکپارچه. The unified, role-aware "my calendar" (meetings + exams +
// assignments + term windows + org events) that is now the PRIMARY /calendar surface
// for student/instructor/parent. The older events+weekly-schedule CalendarPage (R-CAL,
// from More.tsx) stays reachable at /calendar/events for org-event authoring.
const UnifiedCalendarPage = React.lazy(() => import("./pages/UnifiedCalendar"));
// Phase B R1 Commit I (D62 + D61 Constraint #2) — 4 admin pages lazy-
// loaded so they land in a separate admin-academic chunk (configured
// via manualChunks in vite.config.js, also this commit). Main bundle
// delta target: < 50 KB.
const SchoolsAdminPage = React.lazy(() => import("./pages/admin/SchoolsPage"));
const FacultiesAdminPage = React.lazy(() => import("./pages/admin/FacultiesPage"));
const DepartmentsAdminPage = React.lazy(() => import("./pages/admin/DepartmentsPage"));
const ProgramsAdminPage = React.lazy(() => import("./pages/admin/ProgramsPage"));
const PlansAdminPage = React.lazy(() => import("./pages/admin/PlansPage"));
// S-NRI-2 — READ-ONLY viewer for the multi-level ORG tree (وزارت → هلدینگ →
// شرکت → واحد). Own per-route lazy chunk (D66 Path D); not preloaded on anon
// routes. Sibling of the academic-hierarchy pages above, different axis.
const OrganizationsAdminPage = React.lazy(() => import("./pages/admin/OrganizationsPage"));
// Phase B R2 Commit I (D65) — Offerings + Cohorts admin lazy routes.
// Lazy-loading places both pages in admin-academic.{hash}.js per D61
// Constraint #2; main bundle delta target: 0 KB.
const OfferingsAdminPage = React.lazy(() => import("./pages/admin/OfferingsPage"));
const CohortsAdminPage = React.lazy(() => import("./pages/admin/CohortsPage"));
// Phase B R3.a Commit I (D68 + D69) — Identity track admin lazy routes.
// Per D66 Path D: NO admin manualChunks bucket; each page becomes its
// own ProfilesPage-<hash>.js / StudentsPage-<hash>.js / InstructorsPage-<hash>.js
// — none preloaded on anon routes.
const ProfilesAdminPage = React.lazy(() => import("./pages/admin/ProfilesPage"));
const StudentsAdminPage = React.lazy(() => import("./pages/admin/StudentsPage"));
const InstructorsAdminPage = React.lazy(() => import("./pages/admin/InstructorsPage"));
// Phase B R3.b Commit G (D71) — Applications inbox (unified, Q9.a).
// Same per-route lazy pattern; ships as ApplicationsPage-<hash>.js.
const ApplicationsAdminPage = React.lazy(() => import("./pages/admin/ApplicationsPage"));
// S-18 — HR / hiring (استخدام) console; own per-route chunk.
const HrAdminPage = React.lazy(() => import("./pages/admin/HrPage"));
// S-4/5 موج ۲ — regulation-policy (آیین‌نامهٔ آموزشی) admin console; own per-route chunk.
const RegulationAdminPage = React.lazy(() => import("./pages/admin/RegulationPage"));
// Phase B R4 Commit E (D73) — Enrollments admin (closes D72 spine gap).
// Own per-route lazy chunk (D66 Path D).
const EnrollmentsAdminPage = React.lazy(() => import("./pages/admin/EnrollmentsPage"));
// Phase C A.1 (D91) — course content authoring (modules + lessons). Own
// per-route lazy chunk (D66 Path D), not preloaded on anon routes.
const CourseContentAdminPage = React.lazy(() => import("./pages/admin/CourseContentPage"));
// R-SIS-1 — academic terms (نیم‌سال) admin. Own per-route lazy chunk.
const TermsAdminPage = React.lazy(() => import("./pages/admin/TermsPage"));
// R-SIS-2 — curriculum builder (چارت درسی). Own per-route lazy chunk.
const CurriculumBuilderPage = React.lazy(() => import("./pages/CurriculumBuilder"));
// R-SIS-3d — انتخاب واحد + حذف و اضافه (one page, mode from the route).
const RegistrationPage = React.lazy(() => import("./pages/Registration"));
// R-SIS-5b — service desk (inbox/new/detail in one page) + workflow builder.
const ServiceDeskPage = React.lazy(() => import("./pages/ServiceDesk"));
const WorkflowsAdminPage = React.lazy(() => import("./pages/admin/WorkflowsPage"));
// R-SIS — degree policies (caps + probation thresholds) admin.
const DegreePoliciesAdminPage = React.lazy(() => import("./pages/admin/DegreePoliciesPage"));
// Phase C A.4 (D93) — role-aware gradebook. Own lazy chunk (D66 Path D).
const GradebookPage = React.lazy(() => import("./pages/Gradebook"));
// Phase C A.5 (D98) — student transcript. Own lazy chunk (D66 Path D).
const TranscriptPage = React.lazy(() => import("./pages/Transcript"));
// R-PAY-1d — student tuition + admin tariff config.
const TuitionPage = React.lazy(() => import("./pages/Tuition"));
const TuitionConfigAdminPage = React.lazy(() => import("./pages/admin/TuitionConfigPage"));
// R-PAY-2c — student loans + welfare review (role-switched in the page).
const LoansPage = React.lazy(() => import("./pages/Loans"));
// R-PAY-2d — financial clearance (graduation prerequisite).
const ClearancePage = React.lazy(() => import("./pages/Clearance"));
// R-PAY-2e — scholarships / financial aid (role-switched in the page).
const FinancialAidPage = React.lazy(() => import("./pages/FinancialAid"));
// R-PAY-2f — tuition refunds (role-switched in the page).
const RefundPage = React.lazy(() => import("./pages/Refund"));
// R-PAYROLL-1c — payslips (personnel) + decrees admin.
const PayslipsPage = React.lazy(() => import("./pages/Payslips"));
const PayrollDecreesPage = React.lazy(() => import("./pages/admin/PayrollDecrees"));
const PayrollConfigPage = React.lazy(() => import("./pages/admin/PayrollConfig"));
// R-PAYROLL-2a — teaching/supervision claims (instructor + manager queue).
const ClaimsPage = React.lazy(() => import("./pages/Claims"));
const TeachingClaimsPage = React.lazy(() => import("./pages/admin/TeachingClaims"));
// R-PAYROLL-2b — monthly payroll run (manager).
const PayrollRunPage = React.lazy(() => import("./pages/admin/PayrollRun"));
// R-PAYROLL-2c — treasury + reports (manager).
const TreasuryPage = React.lazy(() => import("./pages/admin/Treasury"));
// S-W5/S-W6 — bank withdrawal approval queue (finance_manager / super_admin).
const WithdrawalQueuePage = React.lazy(() => import("./pages/admin/WithdrawalQueue"));
// R-SIS-6 — کمیسیون موارد خاص.
const CommissionPage = React.lazy(() => import("./pages/admin/Commission"));
const MyPathPage = React.lazy(() => import("./pages/MyPath"));
const GradeSubmissionPage = React.lazy(() => import("./pages/GradeSubmission"));
const MyGradesPage = React.lazy(() => import("./pages/MyGrades"));
// R-COMPLY W3 — student's own attendance + the آیین‌نامه ۳/۱۶ over-threshold
// WARNING (flag only; حذف درس stays a human/commission decision). Own lazy chunk.
const MyAttendancePage = React.lazy(() => import("./pages/MyAttendance"));
const StageProgressAdminPage = React.lazy(() => import("./pages/admin/StageProgress"));
// R-SIS-7 — People directory.
const PeoplePage = React.lazy(() => import("./pages/admin/People"));
// R-MON W6 — «پروندهٔ افراد»: the role-aware person-360 by userId (student =
// transcript + audit + exams/assignments/attendance + activity; instructor =
// sections + load + grading + activity). admin/super_admin/faculty_admin
// (faculty-scoped server-side). The default export is PersonOverviewPage.
const StudentOverviewPage = React.lazy(() => import("./pages/admin/StudentOverview"));
// R-ACAD — گرایش (specialization) per-program management. Own lazy chunk.
const SpecializationsPage = React.lazy(() => import("./pages/admin/Specializations"));
// R-ACAD — داده‌های مرجع (FieldGroup + DegreeLevel CRUD). Own lazy chunk.
const ReferenceDataPage = React.lazy(() => import("./pages/admin/ReferenceData"));
// chart-manual.md — کتابخانه وزارت علوم (3268 PDF ministry curricula browser + instantiate).
const MinistryLibraryPage = React.lazy(() => import("./pages/admin/MinistryLibrary"));
const MinistryProgramDetailPage = React.lazy(() => import("./pages/admin/MinistryProgramDetail"));
// §9.3 fast-follow — admin user management (CRUD + role assignment).
const UsersPage = React.lazy(() => import("./pages/admin/UsersPage"));
// R-MON W4 — admin parent↔student link onboarding (the runtime way to connect a
// real والد to a دانشجو; previously seed-only). Own per-route lazy chunk.
const ParentLinksPage = React.lazy(() => import("./pages/admin/ParentLinks"));
const OrgDashboard = React.lazy(() => import("./pages/dashboards/OrgDashboard"));
// University.tsx + Productivity.tsx are multi-export, all workspace:
const SchoolsPage = React.lazy(() => import("./pages/University").then((m) => ({ default: m.SchoolsPage })));
const VirtualLabPage = React.lazy(() => import("./pages/University").then((m) => ({ default: m.VirtualLabPage })));
const LabsPage = React.lazy(() => import("./pages/LabsRich"));
const ResearchPage = React.lazy(() => import("./pages/University").then((m) => ({ default: m.ResearchPage })));
const NotificationsPage = React.lazy(() => import("./pages/Productivity").then((m) => ({ default: m.NotificationsPage })));
// R-CHAT-1b — real messenger (replaces the Productivity mock).
const MessagesPage = React.lazy(() => import("./pages/Messages"));
const BookmarksPage = React.lazy(() => import("./pages/Productivity").then((m) => ({ default: m.BookmarksPage })));
// Student-2 — real achievements (academic milestones) + portfolio (completed
// coursework), replacing the Productivity mock + the ComingSoon placeholder.
const AchievementsPage = React.lazy(() => import("./pages/Achievements"));
const PortfolioPage = React.lazy(() => import("./pages/Portfolio"));
const SubmissionPage = React.lazy(() => import("./pages/Productivity").then((m) => ({ default: m.SubmissionPage })));
// Phase B R3.a Commit H (D68 + D69) — /profile rerouted to the new
// self-service editor at pages/ProfilePage.tsx. The legacy mockup at
// pages/Productivity.tsx ProfilePage export stays in place (dead-code
// once unreferenced; left there to keep Productivity.tsx untouched in
// this commit). Per D66 Path D: own lazy chunk, NO admin bucket.
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
// R-SETTINGS — PUBLIC privacy-gated profile card at /u/:userId. Own lazy chunk;
// route-classification marks the "u" segment PUBLIC so a guest reaches it without
// being bounced to /login (the page + API enforce the owner's privacy settings).
const PublicProfilePage = React.lazy(() => import("./pages/PublicProfile"));
// R-CONNECT — «شبکهٔ من»: the connect hub (/network, authed). Own lazy chunk;
// route-classification leaves it WORKSPACE (auth-gated) by elimination.
const NetworkPage = React.lazy(() => import("./pages/Network"));
// Phase B R6 (D80) — public anon /apply form. Lazy per D66 Path D (not
// cold-paint-critical; a brief Suspense fallback is acceptable). Own
// content-hashed chunk, NOT eager → main-bundle Δ ≈ 0.
const ApplyPage = React.lazy(() => import("./pages/Apply"));
// Phase B R6 (D80) — public anon /track status page (token-based read +
// withdraw). Lazy per D66 Path D; own content-hashed chunk.
const TrackPage = React.lazy(() => import("./pages/Track"));
// R-Notif (D100) — public password-claim landing (/claim?token=). Lazy per
// D66 Path D; own content-hashed chunk, not preloaded on anon routes.
const ClaimPage = React.lazy(() => import("./pages/Claim"));
// R-AUTH-3 — public password-reset landing (/reset?token=). Lazy; own chunk.
const ResetPage = React.lazy(() => import("./pages/Reset"));
// R-NAV-4 (spec §3.5) — the mobile bottom-nav "more" hub. Lazy; its own chunk.
const MoreHubPage = React.lazy(() => import("./pages/MoreHub"));
// R-MARKET P1 — «بازار دانش» authoring studio (course structure only). Two own
// lazy chunks; author-role gated via route-roles.ts ("market" key). The public
// marketplace browse tab is P4 (not added here).
const MarketStudioPage = React.lazy(() => import("./pages/market/Studio"));
const MarketCourseEditorPage = React.lazy(() => import("./pages/market/CourseEditor"));
// R-MARKET P2 — author "preview as a student" page. Own lazy chunk; same
// "market" route-roles key gates it (the whole /market/* subtree).
const MarketCoursePreviewPage = React.lazy(() => import("./pages/market/CoursePreview"));
// R-MARKET P3 — reviewer (content_manager) queue + per-course review page. Own
// lazy chunks; gated to content_manager/admin/super_admin by the "market/review"
// 2-segment key in route-roles.ts (it overrides the author "market" subtree key).
const MarketReviewQueuePage = React.lazy(() => import("./pages/market/ReviewQueue"));
const MarketReviewCoursePage = React.lazy(() => import("./pages/market/ReviewCourse"));
// R-MARKET P4 — the PUBLIC marketplace: catalog (/market) + a course's public
// detail (/market/course/:id). Reachable LOGGED-OUT (route-classification.ts marks
// the bare /market + /market/course/* PUBLIC; the studio/review subtrees stay
// WORKSPACE). Own lazy chunks; no role key (anyone, incl. a guest, may browse).
const MarketplacePage = React.lazy(() => import("./pages/market/Marketplace"));
const MarketCourseDetailPage = React.lazy(() => import("./pages/market/CourseDetail"));
// R-MARKET P5 — pricing/purchase/economics surfaces. «دوره‌های من» (buyer; any
// authed user), «درآمد من» (author), «مدیریت بازار» (admin). Own lazy chunks; the
// "market/my-courses" + "market/earnings" + "market/admin" route-roles keys gate
// them (they win over the public "market" subtree), and route-classification keeps
// them WORKSPACE (auth-gated) — they sit under the /market segment.
const MarketMyCoursesPage = React.lazy(() => import("./pages/market/MyCourses"));
const MarketEarningsPage = React.lazy(() => import("./pages/market/Earnings"));
const MarketAdminPage = React.lazy(() => import("./pages/market/MarketAdmin"));
// R-MARKET EXT — «علاقه‌مندی‌های من» (the buyer's wishlist, /market/wishlist). Own lazy
// chunk; the "market/wishlist" route-roles key opens it to ANY authed role (every role
// may save a course), and route-classification keeps it WORKSPACE (auth-gated).
const MarketWishlistPage = React.lazy(() => import("./pages/market/Wishlist"));
// R-MARKET P6 — the enrolled buyer's COURSE PLAYER (/market/learn/:id). Own lazy chunk;
// the "market/learn" route-roles key opens it to any authed role (the API enforces
// enrollment, 404 for non-buyers) and route-classification keeps it WORKSPACE (auth-gated).
const MarketCoursePlayerPage = React.lazy(() => import("./pages/market/CoursePlayer"));
// R-MARKET P7 (FINAL) — the author's in-browser RECORDING STUDIO (/market/studio/:id/record).
// Own lazy chunk; the author "market" subtree role key gates it (more-specific :id/record route
// resolves ahead of /market/studio/:id). The API enforces course ownership (404 else).
const MarketRecordStudioPage = React.lazy(() => import("./pages/market/RecordStudio"));
// R-MARKET BUNDLES — «بستهٔ دوره» course bundles. The AUTHOR studio list
// (/market/studio/bundles) + bundle builder (/market/studio/bundles/:id) sit under
// the author /market/studio/* subtree (the "market" role key gates them; the more-
// specific /bundles paths resolve ahead of /market/studio/:id). The PUBLIC catalog
// (/market/bundles) + bundle detail (/market/bundles/:id) are reachable LOGGED-OUT —
// "bundles" is NOT an AUTHED_MARKET_SUBPATH, so route-classification keeps them PUBLIC
// like the course catalog. The API @Roles-guards the author routes (404 cross-author).
// R-MARKET OPEN-RESPONSE (Gap 9) — the author's STAFF grading queue for one course
// (/market/grading-queue/:id). Own lazy chunk; gated to the author roles by the
// "market/grading-queue" key in route-roles.ts (the API @Roles-guards + 404s cross-author).
const MarketGradingQueuePage = React.lazy(() => import("./pages/market/GradingQueuePage"));
const MarketStudioBundlesPage = React.lazy(() => import("./pages/market/StudioBundles"));
const MarketBundleEditorPage = React.lazy(() => import("./pages/market/BundleEditor"));
const MarketBundlesPage = React.lazy(() => import("./pages/market/Bundles"));
const MarketBundleDetailPage = React.lazy(() => import("./pages/market/BundleDetail"));

// =====================================================
// useGo — back-compat shim
// =====================================================
//
// The old App.tsx exposed `go(id, param)` to every page; ~140 call
// sites use the signature. We preserve it exactly so no page changes
// during R3 — only App.tsx and the new router.tsx wire react-router.
//
// Future sprints can migrate call sites to useNavigate() directly,
// but that's per-page polish, not the critical path here.
//
// `param` is optional and accepts string | number for ergonomics
// (route ids like course/abc-123 come from string ids; tutorial IDs
// might be numeric in some pages). It's String()-coerced before use.

/**
 * Type of the navigation function passed as `go` prop throughout the
 * app. Pages import this as `import type { Go } from "../router"`.
 */
export type Go = (id: string, param?: string | number | null) => void;

export const useGo = (): Go => {
  const navigate = useNavigate();
  return React.useCallback<Go>(
    (id, param) => {
      const safe = String(id || "home").replace(/^[#/]+/, "");
      const path =
        "/" + safe + (param != null && param !== "" ? "/" + encodeURIComponent(String(param)) : "");
      navigate(path);
      // Mirror the old behaviour: scroll to top on every nav so pages
      // start fresh, never inheriting the previous scroll position.
      try {
        window.scrollTo({ top: 0, behavior: "instant" });
      } catch {
        window.scrollTo(0, 0);
      }
    },
    [navigate]
  );
};

// =====================================================
// useCurrentRoute — mimics the old { id, param } shape
// =====================================================
//
// A few components (Nav, the ErrorBoundary `key`) need the active
// route id + param to drive aria-current / focus-reset semantics.
// We compute it from useLocation() so it stays in sync with whatever
// the router thinks the current URL is.
export interface CurrentRoute {
  id: string;
  param: string | null;
}

export const useCurrentRoute = (): CurrentRoute => {
  const location = useLocation();
  const path = location.pathname.replace(/^\/+/, "");
  if (!path) return { id: "home", param: null };
  const [id, ...rest] = path.split("/");
  return {
    id: id || "home",
    param: rest.length ? decodeURIComponent(rest.join("/")) : null,
  };
};

// =====================================================
// RouteShell — injects `go` + path param into a page
// =====================================================
//
// Each route's element is `<RouteShell Component={SomePage} paramKey="courseId" />`.
// The shell calls hooks once, then renders the page with the same
// prop names the page already uses. paramKey is omitted for routes
// without a URL param.
//
// `Component` is typed as `React.ComponentType<any>` rather than a
// precise prop shape because pages haven't been individually typed
// yet (Phase 15+ retires more @ts-nocheck per file). When the page
// types land, swap to a generic-typed RouteShell<P>.
interface RouteShellProps {
  Component: React.ComponentType<any>;
  paramKey?: string;
}

// Phase-A R7.1.b — Suspense fallback for lazy-loaded route components.
// React.lazy components throw a promise on first render; the Suspense
// boundary catches it and renders the fallback until the chunk loads.
// Eagerly-imported components render synchronously and never trigger
// the Suspense — so the eager PUBLIC + AUTH_FLOW routes are unaffected.
//
// The fallback uses Tailwind utilities for centering + a subtle pulse
// animation. Keep it lightweight (no Radix, no third-party) so it
// itself ships in the main bundle and renders instantly. The styling
// is intentionally close to "blank workspace surface" so the user
// reads it as "loading" not as a content state.
const RouteLoadFallback: React.FC = () => (
  <div
    className="flex items-center justify-center min-h-[60vh] text-[color:var(--fg-mute)] animate-pulse"
    role="status"
    aria-live="polite"
    aria-label="در حال بارگذاری…"
  >
    <div className="text-sm">در حال بارگذاری…</div>
  </div>
);

// S-21 — /dashboard (legacy mock) → the active role's REAL home. None of the
// 14 homeRoutes is "dashboard" (verified by the nav-coherence vitest), so this
// can't loop; an anonymous visitor falls to the auth gate via /login.
const RoleHomeRedirect: React.FC = () => {
  const go = useGo();
  const { activeRoleId } = useRole();
  React.useEffect(() => {
    go(activeRoleId ? (ROLES[activeRoleId]?.homeRoute ?? "progress") : "login");
  }, [go, activeRoleId]);
  return <RouteLoadFallback />;
};

const RouteShell = ({ Component, paramKey }: RouteShellProps): React.ReactElement => {
  const go = useGo();
  const params = useParams();
  const extra: Record<string, string | undefined> = paramKey
    ? { [paramKey]: params[paramKey] }
    : {};
  return (
    <React.Suspense fallback={<RouteLoadFallback />}>
      <Component go={go} {...extra} />
    </React.Suspense>
  );
};

// =====================================================
// Route classification + Layout
// =====================================================
//
// Phase-A R1.1: the inline Layout + route classification that lived
// here were extracted. Layout → apps/web/src/layouts/AppShell.tsx (3-mode
// shell with adaptive sidebar). Route classification →
// apps/web/src/router/route-classification.ts (getRouteKind +
// isPublicRoute / isAuthFlowRoute / isWorkspaceRoute helpers).
//
// We still wrap AppShell in UIRoot here so theme / toast / confirm /
// command-palette / AI FAB context is available everywhere. UIRoot
// previously lived inside Layout; pulling it up means AppShell's three
// modes can compose without owning the global context, and any future
// shell variant (e.g., a print-only shell) gets the same context for free.
const LayoutWithChrome: React.FC = () => {
  const go = useGo();
  return (
    <UIRoot onNavigate={go}>
      <AppShell />
    </UIRoot>
  );
};

// =====================================================
// Route table
// =====================================================
//
// 1:1 with the old `switch (route)` in App.tsx. Three routes carry
// a URL param: `/course-live/:courseId`, `/assessment-live/:assessmentId`,
// `/course/:courseId`, `/virtuallab/:labId`. The catch-all `*` falls
// back to HomePage (matches the old default branch).
//
// Ordering matters for `path="*"` but not for the others (react-router
// resolves the most-specific match first).
const routes = [
  { path: "/", element: <RouteShell Component={HomePage} /> },
  { path: "/home", element: <RouteShell Component={HomePage} /> },
  { path: "/programs", element: <RouteShell Component={ProgramsPage} /> },
  { path: "/catalog", element: <RouteShell Component={CatalogPage} /> },
  { path: "/my-courses", element: <RouteShell Component={MyCoursesPage} /> },
  {
    path: "/course-live/:courseId",
    element: <RouteShell Component={CourseLivePage} paramKey="courseId" />,
  },
  {
    path: "/assessment-live/:assessmentId",
    element: (
      <RouteShell Component={AssessmentLivePage} paramKey="assessmentId" />
    ),
  },
  {
    // W6 — proctored exam room (timed + identity + anti-cheat).
    path: "/exam/:assessmentId",
    element: <RouteShell Component={ExamRoomPage} paramKey="assessmentId" />,
  },
  {
    // R-EXAM-3 (§13.6) — proctor review queue (human-in-the-loop) + item analysis.
    path: "/proctor-review/:assessmentId",
    element: <RouteShell Component={ProctorReviewPage} paramKey="assessmentId" />,
  },
  { path: "/progress", element: <RouteShell Component={ProgressPage} /> },
  // S-19 — the standalone AI tutor page was a near-duplicate of /agents (owner
  // complaint #6). The RAG tutor now lives INSIDE the hub as the «پرسش از جزوه‌ها»
  // kind; /tutor redirects there so old links + the ⌘K/breadcrumb registries stay
  // valid. The feature is preserved (not removed) — just single-origin now.
  { path: "/tutor", element: <Navigate to="/agents?kind=tutor" replace /> },
  { path: "/agents", element: <RouteShell Component={AgentsPage} /> },
  { path: "/classroom", element: <RouteShell Component={ClassroomPage} /> },
  // §12 — live class: sessions hub (list) → lobby (device test) → in-session room.
  { path: "/live-class", element: <RouteShell Component={LiveClassesPage} /> },
  // More-specific /lobby is listed before the bare :sessionId so it matches first.
  {
    path: "/live-class/:sessionId/lobby",
    element: <RouteShell Component={LiveLobbyPage} paramKey="sessionId" />,
  },
  // MS-17 — the post-session «گزارش کلاس» (staff/host only; the endpoint 404s
  // for anyone else). Listed before the bare :sessionId so /report matches first.
  {
    path: "/live-class/:sessionId/report",
    element: <RouteShell Component={SessionReportPage} paramKey="sessionId" />,
  },
  {
    path: "/live-class/:sessionId",
    element: <RouteShell Component={LiveSessionPage} paramKey="sessionId" />,
  },
  { path: "/wallet", element: <RouteShell Component={WalletPage} /> },
  // S-21 — the LEGACY /dashboard was a 100%-mock page (hardcoded sparklines,
  // fabricated schedule/credentials — zero API calls). Every role now has a
  // REAL home (ROLES[role].homeRoute — none of them is "dashboard", so no
  // loop), so /dashboard redirects there. pages/Dashboard.tsx stays on disk
  // and importable (nothing deleted) — it is just no longer routed.
  { path: "/dashboard", element: <RoleHomeRedirect /> },
  // Phase-A R3: dedicated role-home dashboards. role.tsx's homeRoute fields
  // point here so the post-login redirect lands on the correct surface
  // per role. The legacy /dashboard, /instructor, /admin, /parent routes
  // remain for student/instructor/admin/parent respectively.
  { path: "/super", element: <RouteShell Component={SuperAdminDashboard} /> },
  { path: "/content", element: <RouteShell Component={ContentManagerDashboard} /> },
  { path: "/ta", element: <RouteShell Component={TADashboard} /> },
  { path: "/support", element: <RouteShell Component={SupportDashboard} /> },
  { path: "/moderate", element: <RouteShell Component={ModeratorDashboard} /> },
  { path: "/org", element: <RouteShell Component={OrgDashboard} /> },
  { path: "/faculty-dashboard", element: <RouteShell Component={FacultyAdminDashboard} /> },
  // S-NRI-3.5 — organizational/oversight role homes. role.tsx homeRoute points
  // here so post-login lands on the correct NRI surface (not the student page).
  { path: "/ministry-dashboard", element: <RouteShell Component={MinistrySupervisorDashboard} /> },
  { path: "/holding-dashboard", element: <RouteShell Component={HoldingManagerDashboard} /> },
  { path: "/company-dashboard", element: <RouteShell Component={CompanyAdminDashboard} /> },
  { path: "/training-dashboard", element: <RouteShell Component={CompanyTrainingDashboard} /> },
  { path: "/audit-dashboard", element: <RouteShell Component={AuditorDashboard} /> },
  { path: "/certification-desk", element: <RouteShell Component={CertificationDeskDashboard} /> },
  { path: "/assessor-dashboard", element: <RouteShell Component={AssessorDashboard} /> },
  // S-NRI-ADAPT — «سازندهٔ پکیج» → «استودیو دانش» placeholder home.
  { path: "/creator-studio", element: <RouteShell Component={CreatorStudioDashboard} /> },
  {
    // S-21 — the connecting hub; ?tab= selects the section. The old player
    // (CoursePage) renders inside the hub's «محتوا» tab.
    path: "/course/:courseId",
    element: <RouteShell Component={CourseHubPage} paramKey="courseId" />,
  },
  { path: "/course", element: <RouteShell Component={CourseHubPage} /> },
  { path: "/instructor", element: <RouteShell Component={InstructorPage} /> },
  // R-NAV-3 (spec §4.5, §22.1) — retire the mock admissions wizard: redirect
  // to the real multi-step application form. PUBLIC→PUBLIC, so no auth flash.
  { path: "/admissions", element: <Navigate to="/apply" replace /> },
  // Phase B R6 (D80) — public anon application form (PUBLIC route, Q1.a).
  { path: "/apply", element: <RouteShell Component={ApplyPage} /> },
  // Phase B R6 (D80) — public anon status page (token in ?token=, type in ?type=).
  { path: "/track", element: <RouteShell Component={TrackPage} /> },
  // R-Notif (D100) — public password-claim landing (token in ?token=).
  { path: "/claim", element: <RouteShell Component={ClaimPage} /> },
  // R-AUTH-3 — public password-reset landing (token in ?token=).
  { path: "/reset", element: <RouteShell Component={ResetPage} /> },
  { path: "/credential", element: <RouteShell Component={CredentialPage} /> },
  { path: "/search", element: <RouteShell Component={SearchPage} /> },
  { path: "/assessment", element: <RouteShell Component={AssessmentPage} /> },
  { path: "/community", element: <RouteShell Component={CommunityPage} /> },
  { path: "/analytics", element: <RouteShell Component={AnalyticsPage} /> },
  { path: "/authoring", element: <RouteShell Component={AuthoringPage} /> },
  { path: "/recordings", element: <RouteShell Component={RecordingsPage} /> },
  // Phase-15 R7: audit-log viewer. The page itself uses useAbility()
  // to render a denial state for users without read access, and the
  // api enforces the same gate via @Roles + @CheckPolicies. Both
  // layers must agree — defence in depth.
  { path: "/audit", element: <RouteShell Component={AuditPage} /> },
  { path: "/login", element: <RouteShell Component={LoginPage} /> },
  // R-AUTH-1 (spec §5.2, owner decision: application-only) — open
  // registration is retired; /register redirects to the real application
  // funnel. The api also rejects POST /v1/auth/register (server-enforced).
  { path: "/register", element: <Navigate to="/apply" replace /> },
  { path: "/forgot", element: <RouteShell Component={ForgotPage} /> },
  {
    path: "/verify-email",
    element: <RouteShell Component={VerifyEmailPage} />,
  },
  { path: "/2fa-setup", element: <RouteShell Component={TwoFactorPage} /> },
  { path: "/onboarding", element: <RouteShell Component={OnboardingPage} /> },
  { path: "/settings", element: <RouteShell Component={SettingsPage} /> },
  // S-2 nav fix — deep-link into a specific settings section (e.g.
  // /settings/security so the profile "امنیت و نشست‌ها" / 2FA-manage links
  // land on the Security tab instead of dead-ending on Profile). Sibling
  // param route of /settings (see the /course/:courseId pattern); the bare
  // /settings entry above stays registered for the sidebar/⌘K "تنظیمات".
  { path: "/settings/:tab", element: <RouteShell Component={SettingsPage} paramKey="tab" /> },
  // R-CAL2 — the unified, role-aware "my calendar" is the PRIMARY /calendar surface
  // (the "تقویم" nav id resolves here). The older events+weekly CalendarPage stays
  // reachable at /calendar/events for staff org-event authoring; /academic-calendar
  // (term windows) is also still reachable.
  { path: "/calendar", element: <RouteShell Component={UnifiedCalendarPage} /> },
  { path: "/calendar/events", element: <RouteShell Component={CalendarPage} /> },
  { path: "/academic-calendar", element: <RouteShell Component={AcademicCalendarPage} /> },
  { path: "/library", element: <RouteShell Component={LibraryPage} /> },
  { path: "/help", element: <RouteShell Component={HelpPage} /> },
  { path: "/pricing", element: <RouteShell Component={PricingPage} /> },
  // S9 — self-serve subscription checkout (auth-gated as WORKSPACE; any role).
  { path: "/checkout/:planId", element: <RouteShell Component={CheckoutPage} paramKey="planId" /> },
  { path: "/faculty", element: <RouteShell Component={FacultyPage} /> },
  { path: "/admin", element: <RouteShell Component={AdminDashboardPage} /> },
  // Admin governance/ops + super tools (replace the ComingSoon placeholders).
  { path: "/admin/roles", element: <RouteShell Component={RolesPermissionsPage} /> },
  { path: "/admin/calendar", element: <RouteShell Component={AdminCalendarPage} /> },
  { path: "/admin/integrations", element: <RouteShell Component={IntegrationsPage} /> },
  { path: "/admin/notif-templates", element: <RouteShell Component={NotifTemplatesPage} /> },
  { path: "/admin/broadcast", element: <RouteShell Component={BroadcastPage} /> },
  { path: "/ai-governance", element: <RouteShell Component={AiGovernancePage} /> },
  { path: "/monitoring", element: <RouteShell Component={SystemMonitoringPage} /> },
  { path: "/analytics-admin", element: <RouteShell Component={AdminAnalyticsPage} /> },
  { path: "/super/tenants", element: <RouteShell Component={TenantsAdminPage} /> },
  { path: "/super/impersonate", element: <RouteShell Component={ImpersonatePage} /> },
  { path: "/super/multitenancy", element: <RouteShell Component={MultitenancyPage} /> },
  // Support / TA / moderator / content_manager / org / parent (replace ComingSoon).
  { path: "/tickets", element: <RouteShell Component={TicketsQueuePage} /> },
  { path: "/user-lookup", element: <RouteShell Component={UserLookupPage} /> },
  { path: "/grading-queue", element: <RouteShell Component={GradingQueuePage} /> },
  { path: "/flagged-reports", element: <RouteShell Component={FlaggedReportsPage} /> },
  { path: "/community-moderation", element: <RouteShell Component={CommunityModerationPage} /> },
  { path: "/auto-rules", element: <RouteShell Component={AutoRulesPage} /> },
  { path: "/moderation-log", element: <RouteShell Component={ModerationLogPage} /> },
  { path: "/content-review", element: <RouteShell Component={ContentReviewPage} /> },
  { path: "/content-analytics", element: <RouteShell Component={ContentAnalyticsPage} /> },
  { path: "/org/users", element: <RouteShell Component={OrgUsersPage} /> },
  { path: "/org/bulk-enroll", element: <RouteShell Component={OrgBulkEnrollPage} /> },
  { path: "/org/seats", element: <RouteShell Component={OrgSeatsPage} /> },
  { path: "/org/invoices", element: <RouteShell Component={OrgInvoicesPage} /> },
  { path: "/org/reports", element: <RouteShell Component={OrgReportsPage} /> },
  { path: "/parent/grades", element: <RouteShell Component={ParentGradesPage} /> },
  { path: "/parent/attendance", element: <RouteShell Component={ParentAttendancePage} /> },
  { path: "/parent/schedule", element: <RouteShell Component={ParentSchedulePage} /> },
  { path: "/parent/tuition", element: <RouteShell Component={ParentTuitionPage} /> },
  // R-SIS-1 — academic terms (نیم‌سال). Gated to admin+super_admin by the
  // /admin/* subtree default in route-roles.ts.
  { path: "/admin/terms", element: <RouteShell Component={TermsAdminPage} /> },
  // R-SIS-2 — curriculum builder. Gated to faculty_admin+admin+super_admin
  // by the "curriculum-builder" key in route-roles.ts.
  { path: "/curriculum-builder", element: <RouteShell Component={CurriculumBuilderPage} /> },
  // R-MARKET P1 — «بازار دانش» authoring studio. Gated to instructor/
  // content_manager/admin/super_admin by the "market" key in route-roles.ts.
  // The more-specific :id route is a sibling; react-router resolves it first.
  { path: "/market/studio", element: <RouteShell Component={MarketStudioPage} /> },
  // R-MARKET BUNDLES — author bundle studio. The literal /studio/bundles + its :id
  // child rank ahead of the dynamic /market/studio/:id, so react-router resolves them
  // first. Same author "market" role key gates the subtree (the API 404s cross-author).
  { path: "/market/studio/bundles", element: <RouteShell Component={MarketStudioBundlesPage} /> },
  {
    path: "/market/studio/bundles/:id",
    element: <RouteShell Component={MarketBundleEditorPage} paramKey="id" />,
  },
  {
    path: "/market/studio/:id",
    element: <RouteShell Component={MarketCourseEditorPage} paramKey="id" />,
  },
  // R-MARKET P2 — author "preview as a student" (more specific than :id; react-
  // router resolves it first). Same "market" role key gates the subtree.
  {
    path: "/market/studio/:id/preview",
    element: <RouteShell Component={MarketCoursePreviewPage} paramKey="id" />,
  },
  // R-MARKET P7 (FINAL) — in-browser recording studio (more specific than :id; resolved first).
  // Same author "market" role key gates the subtree.
  {
    path: "/market/studio/:id/record",
    element: <RouteShell Component={MarketRecordStudioPage} paramKey="id" />,
  },
  // R-MARKET OPEN-RESPONSE (Gap 9) — author STAFF grading queue for one course. Static
  // 2-segment prefix → resolves ahead of the dynamic /market/course/:id. Gated to the author
  // roles by the "market/grading-queue" key in route-roles.ts; the API 404s a cross-author course.
  {
    path: "/market/grading-queue/:id",
    element: <RouteShell Component={MarketGradingQueuePage} paramKey="id" />,
  },
  // R-MARKET P3 — reviewer queue + per-course review. Gated to content_manager/
  // admin/super_admin by the "market/review" 2-segment key in route-roles.ts.
  { path: "/market/review", element: <RouteShell Component={MarketReviewQueuePage} /> },
  {
    path: "/market/review/:id",
    element: <RouteShell Component={MarketReviewCoursePage} paramKey="id" />,
  },
  // R-MARKET P4 — PUBLIC marketplace. The bare /market catalog + /market/course/:id
  // detail are PUBLIC (browsable LOGGED-OUT); react-router resolves the more-specific
  // /market/studio* + /market/review* siblings above first, so they're unaffected.
  { path: "/market", element: <RouteShell Component={MarketplacePage} /> },
  {
    path: "/market/course/:id",
    element: <RouteShell Component={MarketCourseDetailPage} paramKey="id" />,
  },
  // R-MARKET BUNDLES — PUBLIC bundle catalog (/market/bundles) + bundle detail
  // (/market/bundles/:id). Both reachable LOGGED-OUT (route-classification keeps the
  // "bundles" market sub-path PUBLIC). The literal /market/bundles ranks ahead of the
  // dynamic /market/bundles/:id; neither collides with /market/course/:id.
  { path: "/market/bundles", element: <RouteShell Component={MarketBundlesPage} /> },
  {
    path: "/market/bundles/:id",
    element: <RouteShell Component={MarketBundleDetailPage} paramKey="id" />,
  },
  // R-MARKET P5 — economics surfaces (static 2-segment paths; resolved ahead of the
  // dynamic /market/course/:id). All WORKSPACE (auth-gated) + role-keyed in route-roles.
  { path: "/market/my-courses", element: <RouteShell Component={MarketMyCoursesPage} /> },
  { path: "/market/wishlist", element: <RouteShell Component={MarketWishlistPage} /> },
  { path: "/market/earnings", element: <RouteShell Component={MarketEarningsPage} /> },
  { path: "/market/admin", element: <RouteShell Component={MarketAdminPage} /> },
  // R-MARKET P6 — the enrolled buyer's COURSE PLAYER (static 2-segment prefix → resolves
  // ahead of the dynamic /market/course/:id). WORKSPACE + "market/learn" role key (any
  // authed role); the API 404s non-enrolled callers.
  {
    path: "/market/learn/:id",
    element: <RouteShell Component={MarketCoursePlayerPage} paramKey="id" />,
  },
  // R-SIS-5b — service desk (one page; param drives inbox/new/detail).
  { path: "/service-desk", element: <RouteShell Component={ServiceDeskPage} /> },
  { path: "/service-desk/new", element: <RouteShell Component={ServiceDeskPage} /> },
  { path: "/service-desk/:id", element: <RouteShell Component={ServiceDeskPage} paramKey="id" /> },
  // R-SIS-5b — workflow builder. Gated to admin+super_admin by /admin/* default.
  { path: "/admin/workflows", element: <RouteShell Component={WorkflowsAdminPage} /> },
  // R-SIS — degree policies (caps + probation thresholds). admin/* default gate.
  { path: "/admin/degree-policies", element: <RouteShell Component={DegreePoliciesAdminPage} /> },
  // R-PAY-1d — admin tuition tariff config. admin/* default gate.
  { path: "/admin/tuition-config", element: <RouteShell Component={TuitionConfigAdminPage} /> },
  // Phase B R1 Commit I (D62) — 4 nested admin academic routes (lazy
  // per D61 Constraint #2 — land in admin-academic chunk).
  { path: "/admin/schools", element: <RouteShell Component={SchoolsAdminPage} /> },
  { path: "/admin/faculties", element: <RouteShell Component={FacultiesAdminPage} /> },
  { path: "/admin/departments", element: <RouteShell Component={DepartmentsAdminPage} /> },
  { path: "/admin/programs", element: <RouteShell Component={ProgramsAdminPage} /> },
  { path: "/admin/plans", element: <RouteShell Component={PlansAdminPage} /> },
  // S-NRI-2 — organizational hierarchy viewer (read-only). Gated by the
  // /admin/* subtree default in route-roles.ts (admin + super_admin), which
  // matches the org API's own admin-only integrity check.
  { path: "/admin/organizations", element: <RouteShell Component={OrganizationsAdminPage} /> },
  // Phase B R2 (D65) — Offerings + Cohorts admin routes.
  { path: "/admin/offerings", element: <RouteShell Component={OfferingsAdminPage} /> },
  { path: "/admin/cohorts", element: <RouteShell Component={CohortsAdminPage} /> },
  // Phase B R3.a Commit I (D68 + D69) — Identity admin routes.
  { path: "/admin/profiles", element: <RouteShell Component={ProfilesAdminPage} /> },
  { path: "/admin/students", element: <RouteShell Component={StudentsAdminPage} /> },
  { path: "/admin/instructors", element: <RouteShell Component={InstructorsAdminPage} /> },
  // Phase B R3.b Commit G (D71) — Applications inbox.
  { path: "/admin/applications", element: <RouteShell Component={ApplicationsAdminPage} /> },
  { path: "/admin/hr", element: <RouteShell Component={HrAdminPage} /> },
  // Phase B R4 Commit E (D73) — Enrollments admin.
  { path: "/admin/enrollments", element: <RouteShell Component={EnrollmentsAdminPage} /> },
  // Phase C A.1 (D91) — course content authoring (picker + per-course editor).
  { path: "/admin/course-content", element: <RouteShell Component={CourseContentAdminPage} /> },
  {
    path: "/admin/course-content/:courseId",
    element: <RouteShell Component={CourseContentAdminPage} paramKey="courseId" />,
  },
  // Phase C A.4 (D93) — gradebook (picker + per-course; role-aware view).
  { path: "/gradebook", element: <RouteShell Component={GradebookPage} /> },
  {
    path: "/gradebook/:courseId",
    element: <RouteShell Component={GradebookPage} paramKey="courseId" />,
  },
  { path: "/parent", element: <RouteShell Component={ParentDashboard} /> },
  {
    path: "/officehours",
    element: <RouteShell Component={OfficeHoursPage} />,
  },
  { path: "/events", element: <RouteShell Component={EventsPage} /> },
  { path: "/about", element: <RouteShell Component={AboutPage} /> },
  { path: "/schools", element: <RouteShell Component={SchoolsPage} /> },
  { path: "/labs", element: <RouteShell Component={LabsPage} /> },
  {
    path: "/virtuallab/:labId",
    element: <RouteShell Component={VirtualLabPage} paramKey="labId" />,
  },
  { path: "/virtuallab", element: <RouteShell Component={VirtualLabPage} /> },
  { path: "/research", element: <RouteShell Component={ResearchPage} /> },
  // R-NAV-4 (spec §3.4/3.5) — real "more" hub (was a broken bottom-nav slot
  // that fell through to the landing page).
  { path: "/more", element: <RouteShell Component={MoreHubPage} /> },
  { path: "/inbox", element: <RouteShell Component={NotificationsPage} /> },
  { path: "/messages", element: <RouteShell Component={MessagesPage} /> },
  { path: "/bookmarks", element: <RouteShell Component={BookmarksPage} /> },
  {
    path: "/achievements",
    element: <RouteShell Component={AchievementsPage} />,
  },
  { path: "/portfolio", element: <RouteShell Component={PortfolioPage} /> },
  { path: "/submission", element: <RouteShell Component={SubmissionPage} /> },
  { path: "/profile", element: <RouteShell Component={ProfilePage} /> },
  // R-SETTINGS — PUBLIC privacy-gated profile card. PUBLIC route (see
  // route-classification "u"); the page handles 404 (private) / 401 (members-only
  // for a guest) / 200 (visible) entirely from the API response.
  {
    path: "/u/:userId",
    element: <RouteShell Component={PublicProfilePage} paramKey="userId" />,
  },
  // R-CONNECT — «شبکهٔ من» (connect hub). WORKSPACE (auth-gated by elimination);
  // open to any authenticated role (no route-roles key — it's a shared account
  // surface like /messages, /inbox).
  { path: "/network", element: <RouteShell Component={NetworkPage} /> },
  { path: "/transcript", element: <RouteShell Component={TranscriptPage} /> },
  // R-PAY-1d — student tuition balance + pay.
  { path: "/tuition", element: <RouteShell Component={TuitionPage} /> },
  // R-PAY-2c — student loans / welfare review.
  { path: "/tuition/loans", element: <RouteShell Component={LoansPage} /> },
  // R-PAY-2d — financial clearance.
  { path: "/tuition/clearance", element: <RouteShell Component={ClearancePage} /> },
  // R-PAY-2f — tuition refunds.
  { path: "/tuition/refund", element: <RouteShell Component={RefundPage} /> },
  // R-PAYROLL-1c — payslips + decrees.
  { path: "/payroll/my-payslips", element: <RouteShell Component={PayslipsPage} /> },
  { path: "/admin/finance/decrees", element: <RouteShell Component={PayrollDecreesPage} /> },
  { path: "/admin/finance/withdrawals", element: <RouteShell Component={WithdrawalQueuePage} /> },
  { path: "/admin/finance/payroll-config", element: <RouteShell Component={PayrollConfigPage} /> },
  // R-PAYROLL-2a — teaching claims (instructor self-service + manager queue).
  { path: "/payroll/claims", element: <RouteShell Component={ClaimsPage} /> },
  { path: "/admin/finance/claims", element: <RouteShell Component={TeachingClaimsPage} /> },
  { path: "/admin/finance/payroll", element: <RouteShell Component={PayrollRunPage} /> },
  { path: "/admin/finance/treasury", element: <RouteShell Component={TreasuryPage} /> },
  { path: "/admin/commission", element: <RouteShell Component={CommissionPage} /> },
  { path: "/admin/regulation", element: <RouteShell Component={RegulationAdminPage} /> },
  { path: "/my-path", element: <RouteShell Component={MyPathPage} /> },
  { path: "/grade-submission", element: <RouteShell Component={GradeSubmissionPage} /> },
  { path: "/my-grades", element: <RouteShell Component={MyGradesPage} /> },
  // R-COMPLY W3 — student's own attendance (per-section tally + ۳/۱۶ warning).
  { path: "/my-attendance", element: <RouteShell Component={MyAttendancePage} /> },
  { path: "/admin/stage-progress", element: <RouteShell Component={StageProgressAdminPage} /> },
  { path: "/admin/people", element: <RouteShell Component={PeoplePage} /> },
  // R-MON W6 — «پروندهٔ افراد»: role-aware person-360 (student/instructor),
  // faculty_admin scoped server-side. Route id kept stable as the known entry.
  { path: "/admin/student-overview", element: <RouteShell Component={StudentOverviewPage} /> },
  // R-ACAD — گرایش management (program picker → per-program CRUD).
  { path: "/admin/specializations", element: <RouteShell Component={SpecializationsPage} /> },
  // R-ACAD — داده‌های مرجع (گروه‌های آموزشی + مقاطع تحصیلی CRUD).
  { path: "/admin/reference-data", element: <RouteShell Component={ReferenceDataPage} /> },
  // chart-manual.md — کتابخانه وزارت علوم (3268 PDF browser + detail + instantiate).
  { path: "/admin/ministry-library", element: <RouteShell Component={MinistryLibraryPage} /> },
  { path: "/admin/ministry-library/:id", element: <RouteShell Component={MinistryProgramDetailPage} /> },
  { path: "/admin/users", element: <RouteShell Component={UsersPage} /> },
  // R-MON W4 — admin parent↔student link onboarding (admin/super_admin/support).
  { path: "/admin/parent-links", element: <RouteShell Component={ParentLinksPage} /> },
  {
    path: "/degree-audit",
    element: <RouteShell Component={DegreeAuditPage} />,
  },
  // چارت درسی من — student self-view of the full گرایش-filtered chart.
  { path: "/my-chart", element: <RouteShell Component={MyChartPage} /> },
  // Student services — real interactive pages (replace the ComingSoon placeholders).
  { path: "/cognitive-profile", element: <RouteShell Component={CognitiveProfilePage} /> },
  { path: "/adaptive-path", element: <RouteShell Component={AdaptivePathPage} /> },
  { path: "/assignments", element: <RouteShell Component={AssignmentsPage} /> },
  // Instructor teaching tools (replace the ComingSoon placeholders).
  { path: "/instructor-dashboard", element: <RouteShell Component={InstructorDashboardPage} /> },
  { path: "/question-bank", element: <RouteShell Component={QuestionBankPage} /> },
  { path: "/assessment-builder", element: <RouteShell Component={AssessmentBuilderPage} /> },
  { path: "/rubric-builder", element: <RouteShell Component={RubricBuilderPage} /> },
  { path: "/roster", element: <RouteShell Component={ClassRosterPage} /> },
  { path: "/attendance", element: <RouteShell Component={AttendancePage} /> },
  { path: "/class-analytics", element: <RouteShell Component={ClassAnalyticsPage} /> },
  { path: "/item-analysis", element: <RouteShell Component={ItemAnalysisPage} /> },
  { path: "/copilot", element: <RouteShell Component={InstructorCopilotPage} /> },
  // Faculty-admin teaching/ops tools (replace the ComingSoon placeholders).
  { path: "/admin/courses", element: <RouteShell Component={CourseCatalogMasterPage} /> },
  { path: "/instructor-assignment", element: <RouteShell Component={InstructorAssignmentPage} /> },
  { path: "/probation-review", element: <RouteShell Component={ProbationReviewPage} /> },
  { path: "/faculty-reports", element: <RouteShell Component={FacultyReportsPage} /> },
  { path: "/schedule", element: <RouteShell Component={WeeklySchedulePage} /> },
  {
    path: "/registration",
    element: <RouteShell Component={RegistrationPage} />,
  },
  // R-SIS-3d — حذف و اضافه shares the RegistrationPage (mode from the route id).
  {
    path: "/add-drop",
    element: <RouteShell Component={RegistrationPage} />,
  },
  { path: "/career", element: <RouteShell Component={CareerPage} /> },
  {
    path: "/financial-aid",
    element: <RouteShell Component={FinancialAidPage} />,
  },
  { path: "/wellness", element: <RouteShell Component={WellnessPage} /> },
  // WELL-1 — wellness test catalogue (grouped by category). Static segment, so
  // it resolves ahead of /wellness/test/:id.
  { path: "/wellness/tests", element: <RouteShell Component={WellnessTestsPage} /> },
  // WELL-1 — take a wellness self-screening test. testId in the path param.
  {
    path: "/wellness/test/:id",
    element: <RouteShell Component={WellnessTestPage} paramKey="id" />,
  },
  // WELL-1 — self-only result history (static segment → resolves ahead of
  // /wellness/result/:id) + one result detail (resultId in the path param).
  { path: "/wellness/results", element: <RouteShell Component={WellnessResultsPage} /> },
  {
    path: "/wellness/result/:id",
    element: <RouteShell Component={WellnessResultPage} paramKey="id" />,
  },
  // WELL-4 — counselor (مشاور دانشگاه) workspace. The inbox is the role's home;
  // it hosts the own-appointments queue + the open crisis queue + scoped student
  // summaries. route-roles.ts gates all three to counselor/admin/super_admin.
  // The two sibling sidebar destinations render the SAME page; CounselorInbox
  // self-selects the active tab from the route id (useCurrentRoute), so the
  // counselor nav is fully live + coherent (no dead-end placeholders).
  { path: "/counselor-inbox", element: <RouteShell Component={CounselorInboxPage} /> },
  { path: "/counseling-appointments", element: <RouteShell Component={CounselorInboxPage} /> },
  { path: "/crisis-queue", element: <RouteShell Component={CounselorInboxPage} /> },
  // R-MON W9 — counselor caseload / monitoring. route-roles.ts gates it to
  // counselor/admin/super_admin; the backend scopes every read to the caller's
  // OWN caseload + LINKED students (404 no-leak).
  { path: "/counselor-caseload", element: <RouteShell Component={CounselorCaseloadPage} /> },
  { path: "/alumni", element: <RouteShell Component={AlumniPage} /> },
  { path: "/hackathons", element: <RouteShell Component={HackathonsPage} /> },
  { path: "/honor-code", element: <RouteShell Component={HonorCodePage} /> },
  // R-NAV-2 — "coming soon" placeholders for the redesigned sidebars'
  // not-yet-built destinations. Generated from PLACEHOLDER_ROUTES so the
  // route table and the sidebar links share one source of truth. Each key is
  // a 1- or 2-segment path; ComingSoon derives its title from the same map.
  // Placed before the catch-all so they match ahead of "*".
  ...Object.keys(PLACEHOLDER_ROUTES).map((key) => ({
    path: "/" + key,
    element: <RouteShell Component={ComingSoonPage} />,
  })),
];

// S-21 — the registered path list, exported for the nav-coherence vitest
// (tests/nav/nav-coherence.test.ts): every sidebar / bottom-nav / command
// link must resolve to one of these paths, so a renamed route that leaves a
// stale nav link behind fails the BLOCKING gate instead of shipping a
// dead-end. Paths only (no elements) — importing this stays side-effect-light.
export const ROUTE_PATHS: readonly string[] = routes.map((r) => r.path);

const router = createBrowserRouter([
  {
    element: <LayoutWithChrome />,
    children: routes,
  },
  // R-NAV-6 (spec §4.10) — catch-all 404 as a TOP-LEVEL sibling, OUTSIDE the
  // AppShell layout. Known paths match the layout's children first (more
  // specific); only genuinely-unknown paths fall here. Standalone so the
  // workspace auth gate can't bounce an anonymous visitor to /login.
  { path: "*", element: <NotFoundPage /> },
]);

export const AppRouter: React.FC = () => <RouterProvider router={router} />;
